// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The board CRDT — a conflict-free replicated data type for a shared drawing.
 *
 * It is an **LWW-element-set of shapes** made of three commutative, idempotent
 * operation kinds:
 *
 *   add    — an add-wins register keyed by the shape id. A live-streamed stroke
 *            re-sends the SAME id with more points; the op carrying the highest
 *            Lamport stamp wins, so the finished stroke always beats its partials.
 *   remove — a tombstone: a removed id is hidden forever (object eraser / undo).
 *   clear  — a monotonic barrier at a Lamport time; every shape stamped at or
 *            before it is hidden. Only the largest barrier is kept.
 *
 * A **Lamport clock** gives every op a total order (tie-broken by author id), used
 * for both z-ordering and last-writer-wins. Because every op is keyed by identity
 * and merging is a set union, applying the same op twice is a no-op and applying a
 * peer's whole op-log in ANY order converges to the identical board — which is what
 * lets a newcomer receive the full state as an unordered bundle and match everyone.
 *
 * Everything here is pure (no DOM, no network, no crypto) and unit-tested directly.
 */
import type { Op, Shape } from './types.ts';

/** The shared logical board size. Every client renders THIS, scaled to fit. */
export const BOARD_W = 1600;
export const BOARD_H = 1000;

/** True if shape `a` should win a same-id conflict against shape `b`. */
export function shapeWins(a: Shape, b: Shape): boolean {
  if (a.lamport !== b.lamport) return a.lamport > b.lamport;
  return a.author > b.author;
}

export class Board {
  private adds = new Map<string, Shape>();
  private removed = new Set<string>();
  private clearBarrier = 0;
  /** Lamport clock — max stamp seen; a new local op takes clock + 1. */
  private clock = 0;

  /** Advance the clock past a stamp we have observed on the wire. */
  observe(lamport: number): void {
    if (lamport > this.clock) this.clock = lamport;
  }

  /** Mint the next Lamport stamp for a local op. */
  tick(): number {
    this.clock += 1;
    return this.clock;
  }

  /** Current clock value (read-only). */
  now(): number {
    return this.clock;
  }

  /**
   * Apply one op. Returns true if the VISIBLE board changed (so the caller can
   * decide whether to re-render). Idempotent and order-independent.
   */
  apply(op: Op): boolean {
    switch (op.t) {
      case 'add': {
        this.observe(op.shape.lamport);
        const prev = this.adds.get(op.shape.id);
        if (prev && !shapeWins(op.shape, prev)) return false; // older/duplicate
        this.adds.set(op.shape.id, op.shape);
        // Visible only if not tombstoned and above the clear barrier.
        return !this.removed.has(op.shape.id) && op.shape.lamport > this.clearBarrier;
      }
      case 'remove': {
        this.observe(op.lamport);
        if (this.removed.has(op.id)) return false;
        this.removed.add(op.id);
        const s = this.adds.get(op.id);
        return !!s && s.lamport > this.clearBarrier; // was it visible?
      }
      case 'clear': {
        this.observe(op.lamport);
        if (op.lamport <= this.clearBarrier) return false;
        this.clearBarrier = op.lamport; // advancing the barrier always resets the board
        return true;
      }
    }
  }

  /** Apply many ops (e.g. a sync bundle). True if any changed the board. */
  applyMany(ops: Op[]): boolean {
    let changed = false;
    for (const op of ops) if (this.apply(op)) changed = true;
    return changed;
  }

  /** The visible shapes, in draw order (oldest first, newest on top). */
  visible(): Shape[] {
    const out: Shape[] = [];
    for (const s of this.adds.values()) {
      if (this.removed.has(s.id)) continue;
      if (s.lamport <= this.clearBarrier) continue;
      out.push(s);
    }
    out.sort((a, b) => (a.lamport !== b.lamport ? a.lamport - b.lamport : a.author < b.author ? -1 : a.author > b.author ? 1 : 0));
    return out;
  }

  /** Number of currently-visible shapes. */
  count(): number {
    return this.visible().length;
  }

  isEmpty(): boolean {
    return this.count() === 0;
  }

  /** Look up the currently-stored shape for an id, if any. */
  get(id: string): Shape | undefined {
    const s = this.adds.get(id);
    if (!s || this.removed.has(s.id) || s.lamport <= this.clearBarrier) return undefined;
    return s;
  }

  /**
   * A minimal op-log that reproduces the current state — sent to a newcomer so
   * their board matches. Emitting the reconstructed set (not a growing history)
   * keeps the bundle bounded to the live drawing.
   */
  snapshotOps(): Op[] {
    const ops: Op[] = [];
    if (this.clearBarrier > 0) ops.push({ t: 'clear', author: '', lamport: this.clearBarrier });
    for (const s of this.adds.values()) ops.push({ t: 'add', shape: s });
    for (const id of this.removed) ops.push({ t: 'remove', id, author: '', lamport: 0 });
    return ops;
  }
}
